import { useState, useContext, useEffect } from "react";
import { parseCookies } from "nookies";

import { Button, Paper, TextField, Text } from "../../core";
import { AuthContext } from "../../../contexts/AuthContext";
import { AppContext } from "../../../contexts/AppContext";
import { CustomSnackBar } from "../../core/Snackbar";

const TOTP_COOKIE = "fundamentos.totpToken";

function getTotpErrorMessage(error) {
  const status = error?.status || error?.statusCode;
  if (status === 429 || error?.message?.includes("Muitas tentativas")) {
    return (
      error?.message ||
      "Muitas tentativas. Tente novamente em alguns minutos."
    );
  }
  return "Código inválido ou expirado";
}

function normalizeTotpCode(value) {
  return value.replace(/\D/g, "").slice(0, 6);
}

export default function TotpVerify() {
  const { verifyTotp, isAuthenticated, history } = useContext(AuthContext);
  const { setLoading } = useContext(AppContext);

  const [code, setCode] = useState("");
  const [statusMessage, setStatusMessage] = useState({
    severity: "error",
    message: "",
  });
  const [showSnackBar, setShowSnackBar] = useState(false);

  useEffect(() => {
    const { [TOTP_COOKIE]: totpToken } = parseCookies();
    if (!totpToken) {
      history.push("/");
    }
  }, [history]);

  useEffect(() => {
    if (isAuthenticated) {
      history.push("/app");
    }
  }, [history, isAuthenticated]);

  const handleCodeChange = (event) => {
    setCode(normalizeTotpCode(event.target.value));
  };

  const handleCodePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    setCode(normalizeTotpCode(pasted));
  };

  const submitForm = async (event) => {
    event.preventDefault();

    if (code.length !== 6) {
      setStatusMessage({
        severity: "warning",
        message: "Informe o código de 6 dígitos do autenticador.",
      });
      setShowSnackBar(true);
      return;
    }

    setLoading(true);
    try {
      await verifyTotp(code);
      history.push("/app");
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message: getTotpErrorMessage(error),
      });
      setShowSnackBar(true);
    } finally {
      setLoading(false);
    }
  };

  const redirectToLogin = (event) => {
    event.preventDefault();
    history.push("/");
  };

  return (
    <div style={styles.container}>
      <form onSubmit={submitForm}>
        <Paper style={styles.loginBox} elevation={6}>
          <div style={styles.logoContainer}>
            <img
              src="logotipo-fundamentos.png"
              style={styles.logo}
              alt="Fundamentos"
            />
          </div>

          <Text style={styles.instructions}>
            Digite o código de 6 dígitos do seu app autenticador (Google
            Authenticator, Authy ou similar) para concluir o login.
          </Text>

          <TextField
            variant="outlined"
            size="small"
            label="Código de verificação"
            style={styles.loginField}
            value={code}
            name="code"
            onChange={handleCodeChange}
            onPaste={handleCodePaste}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 6,
              "data-testid": "totp-verify-code-input",
            }}
            autoFocus
          />

          <Button
            style={styles.loginButton}
            variant="contained"
            color="primary"
            size="medium"
            type="submit"
          >
            Verificar
          </Button>

          <div style={styles.buttonsContainer}>
            <Button
              color="primary"
              style={{ color: "#000" }}
              size="small"
              onClick={redirectToLogin}
            >
              Voltar para login
            </Button>
          </div>
        </Paper>
      </form>

      {showSnackBar && (
        <CustomSnackBar
          severity={statusMessage.severity}
          message={statusMessage.message}
          open={showSnackBar}
          handleClose={() => setShowSnackBar(false)}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  loginBox: {
    padding: 20,
    margin: 10,
    maxWidth: 360,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    margin: 25,
  },
  logo: {
    maxWidth: 150,
  },
  instructions: {
    marginBottom: 16,
    lineHeight: 1.5,
  },
  loginField: {
    marginTop: 10,
  },
  loginButton: {
    width: "100%",
    height: 35,
    marginTop: 20,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 10,
  },
};
